/**
 * A small picture (emoji) for each item, picked from its name, keywords and category, so a
 * long receipt is quicker to scan. You can set your own per product in the catalog.
 */
import type { CatalogNote } from "./types.ts";

/** First match wins, so more specific words come first. */
const RULES: [RegExp, string][] = [
  [/membership|annual fee|\bfee\b/, "💳"],
  [/carried over|earlier balance|balance/, "↩️"],
  [/strawberr/, "🍓"],
  [/blueberr|blackberr|raspberr|berries/, "🫐"],
  [/avocado/, "🥑"],
  [/banana/, "🍌"],
  [/grape/, "🍇"],
  [/persimmon|orange|mandarin|clementine|tangerine/, "🍊"],
  [/apple/, "🍎"],
  [/mango|pickd orgmng/, "🥭"],
  [/plum|cherr/, "🍒"],
  [/lemon|lime/, "🍋"],
  [/peach/, "🍑"],
  [/watermelon|melon/, "🍉"],
  [/cucumber/, "🥒"],
  [/spinach|romaine|lettuce|spring mix|salad greens|kale|greens/, "🥬"],
  [/caesar|salad/, "🥗"],
  [/tomato/, "🍅"],
  [/potato chips|chips|crisps/, "🥔"],
  [/carrot/, "🥕"],
  [/corn/, "🌽"],
  [/broccoli/, "🥦"],
  [/mushroom/, "🍄"],
  [/onion|garlic/, "🧄"],
  [/seaweed|nori/, "🍙"],
  [/ramen|ramyun|noodle|pho|udon|dac biet|beef stew/, "🍜"],
  [/rice/, "🍚"],
  [/dumpling|xiao long|dim sum|din tai fung|dinner/, "🥟"],
  [/sushi/, "🍣"],
  [/pizza/, "🍕"],
  [/burger/, "🍔"],
  [/taco|burrito|taco bell/, "🌮"],
  [/hot dog/, "🌭"],
  [/chicken bake|chicken|tender|thigh|breast|rotisserie/, "🍗"],
  [/steak|flank|beef|pork|chop|ribs|bacon|spam|ham/, "🥩"],
  [/salmon|tuna|sardine|fish|shrimp|crab|lobster/, "🐟"],
  [/egg/, "🥚"],
  [/cheese/, "🧀"],
  [/milk|yogurt|want want|dairy/, "🥛"],
  [/butter/, "🧈"],
  [/croissant/, "🥐"],
  [/bread|rustic italian|bagel|baguette|loaf|toast/, "🍞"],
  [/muffin|cupcake|cake|pastry/, "🧁"],
  [/cookie|biscuit/, "🍪"],
  [/chocolate|candy|mints?\b|pmint|gum/, "🍬"],
  [/popcorn/, "🍿"],
  [/peanut butter|pb pouch|nut|almond|cashew/, "🥜"],
  [/honey/, "🍯"],
  [/oil|olive oil|sesame oil|pesto|sauce|dressing/, "🫒"],
  [/tru fru|frozen|ice cream|gelato/, "🍨"],
  [/coffee|espresso|latte/, "☕"],
  [/tea|matcha/, "🍵"],
  [/juice|soda|poppi|sparkling|drink|water|lemonade/, "🥤"],
  [/beer/, "🍺"],
  [/wine/, "🍷"],
  [/fridge|refrigerator|microwave|appliance|tv|television|monitor|laptop|electronics/, "🔌"],
  [/scissors|tool|tape/, "✂️"],
  [/paper towel|toilet paper|tissue/, "🧻"],
  [/soap|detergent|shampoo|cleaner|wipes/, "🧼"],
  [/battery|batteries/, "🔋"],
  [/shirt|clothes|marshalls|jacket|socks|shoes/, "👕"],
  [/party|halloween|ticket|event/, "🎉"],
  [/gas|fuel/, "⛽"],
  [/gift/, "🎁"],
  [/flower|plant/, "🌷"],
  [/pet|dog food|cat food/, "🐾"],
  [/medicine|vitamin|pharmacy/, "💊"],
];

const BY_CATEGORY: Record<string, string> = {
  groceries: "🛒",
  restaurants: "🍽️",
  household: "🏠",
  fees: "💳",
  fun: "🎉",
  balance: "↩️",
  travel: "✈️",
  transport: "🚗",
  clothing: "👕",
  electronics: "🔌",
  health: "💊",
};

// Product names repeat a lot (the same item bought every trip), so remember the answers.
const seen = new Map<string, string | null>();
const SEEN_MAX = 5000;

function ruleIcon(text: string): string | null {
  const hit = seen.get(text);
  if (hit !== undefined) return hit;
  let icon: string | null = null;
  for (const [re, i] of RULES) {
    if (re.test(text)) {
      icon = i;
      break;
    }
  }
  if (seen.size >= SEEN_MAX) seen.clear();
  seen.set(text, icon);
  return icon;
}

export function itemIcon(name: string, category: string, note?: CatalogNote | null): string {
  if (note?.icon) return note.icon;
  const kw = note?.keywords;
  const text = (kw?.length ? `${name} ${kw.join(" ")}` : name).toLowerCase();
  return ruleIcon(text) ?? BY_CATEGORY[category.toLowerCase()] ?? "🛍️";
}
